/**
 * Dispute Centre server functions.
 *
 * All eligibility, authorization, refund amounts, connected-account ids and
 * Stripe identifiers are derived server-side. Nothing sensitive is returned
 * to the browser. Payout / refund side effects delegate to the shared
 * performOrderPayout + refundOrderViaStripe helpers so the Stripe
 * idempotency and audit story stays in one place.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const ACTIVE_STATUSES = ["open", "awaiting_seller", "awaiting_buyer", "under_review"] as const;

const REASONS = [
  "item_not_received",
  "item_not_as_described",
  "damaged_item",
  "wrong_item",
  "suspected_counterfeit",
  "missing_contents",
  "tracking_problem",
  "other",
] as const;

const REQUESTED_RESOLUTIONS = ["full_refund", "partial_refund", "replacement", "other"] as const;

const ALLOWED_MIMES = ["image/jpeg", "image/png", "image/webp", "application/pdf"] as const;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

type DisputeRole = "buyer" | "seller" | "admin";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function resolveRole(supabase: any, userId: string, disputeRow: { buyer_id: string; seller_id: string }): Promise<DisputeRole> {
  const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (isAdmin) return "admin";
  if (userId === disputeRow.buyer_id) return "buyer";
  if (userId === disputeRow.seller_id) return "seller";
  throw new Error("Not authorized for this dispute");
}


/** Buyer opens a dispute. Delegates atomicity + eligibility to the SQL fn. */
export const openDispute = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      orderId: z.string().uuid(),
      reason: z.enum(REASONS),
      description: z.string().trim().min(10).max(4000),
      requestedResolution: z.enum(REQUESTED_RESOLUTIONS),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: id, error } = await supabase.rpc("open_dispute_v2" as never, {
      _order_id: data.orderId,
      _reason: data.reason,
      _description: data.description,
      _requested_resolution: data.requestedResolution,
    } as never);
    if (error) throw new Error(error.message);
    return { disputeId: id as unknown as string };
  });

/** List disputes visible to the caller. */
export const listDisputes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });

    let q = supabase
      .from("disputes")
      .select("id,order_id,buyer_id,seller_id,reason,requested_resolution,status,created_at,updated_at,resolved_at,orders(id,amount_pence,auction_id,auctions(title,images))")
      .order("created_at", { ascending: false })
      .limit(500);
    if (!isAdmin) {
      q = q.or(`buyer_id.eq.${userId},seller_id.eq.${userId}`);
    }
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return { disputes: data ?? [], isAdmin: !!isAdmin };
  });

/** Load a single dispute with messages, evidence and order info. */
export const getDispute = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ disputeId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: dispute, error } = await supabase
      .from("disputes")
      .select("id,order_id,buyer_id,seller_id,opened_by,reason,description,requested_resolution,status,admin_resolution,resolution_notes,refund_amount_pence,created_at,updated_at,resolved_at,closed_at,orders(id,amount_pence,shipping_method,shipping_cost_pence,tracking_number,status,has_active_dispute,stripe_transfer_id,payout_released_at,auction_id,auctions(title,images))")
      .eq("id", data.disputeId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!dispute) throw new Error("Dispute not found");

    const role = await resolveRole(supabase, userId, dispute);

    const { data: messages } = await supabase
      .from("dispute_messages")
      .select("id,sender_user_id,sender_role,message,is_internal_admin_note,created_at")
      .eq("dispute_id", data.disputeId)
      .order("created_at", { ascending: true });

    const { data: evidence } = await supabase
      .from("dispute_evidence")
      .select("id,uploaded_by_user_id,uploader_role,file_name,mime_type,file_size,caption,created_at")
      .eq("dispute_id", data.disputeId)
      .order("created_at", { ascending: true });

    // Names for the timeline (best-effort, non-sensitive: display names only)
    const userIds = Array.from(new Set([
      dispute.buyer_id,
      dispute.seller_id,
      ...(messages ?? []).map((m) => m.sender_user_id),
      ...(evidence ?? []).map((e) => e.uploaded_by_user_id),
    ]));
    const { data: profiles } = await supabase
      .from("public_profiles" as never)
      .select("id,display_name")

      .in("id", userIds);
    const nameMap: Record<string, string> = {};
    for (const p of (profiles ?? []) as Array<{ id: string; display_name: string | null }>) nameMap[p.id] = p.display_name ?? "User";

    return {
      dispute,
      messages: messages ?? [],
      evidence: evidence ?? [],
      names: nameMap,
      role,
    };
  });

/** Buyer, seller or admin posts a message on the dispute. */
export const postDisputeMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      disputeId: z.string().uuid(),
      message: z.string().trim().min(1).max(4000),
      isInternal: z.boolean().optional().default(false),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: id, error } = await supabase.rpc("post_dispute_message" as never, {
      _dispute_id: data.disputeId,
      _message: data.message,
      _is_internal_admin_note: data.isInternal,
    } as never);
    if (error) throw new Error(error.message);
    return { id: id as unknown as string };
  });

/** Upload evidence file (base64-encoded on the client). Server validates
 *  MIME/size, generates a random secure filename, writes into a private
 *  bucket via service role, and records metadata. */
export const uploadDisputeEvidence = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      disputeId: z.string().uuid(),
      fileName: z.string().min(1).max(255),
      mimeType: z.enum(ALLOWED_MIMES),
      fileBase64: z.string().min(4).max(20_000_000), // ~14 MB base64 ceiling
      caption: z.string().max(500).optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Load dispute + verify role
    const { data: dispute, error: dErr } = await supabase
      .from("disputes")
      .select("id,buyer_id,seller_id,status")
      .eq("id", data.disputeId)
      .maybeSingle();
    if (dErr) throw new Error(dErr.message);
    if (!dispute) throw new Error("Dispute not found");
    const role = await resolveRole(supabase, userId, dispute);
    if (role !== "admin" && !ACTIVE_STATUSES.includes(dispute.status as typeof ACTIVE_STATUSES[number])) {
      throw new Error("Dispute is closed");
    }

    // Decode + size check
    const buffer = Buffer.from(data.fileBase64, "base64");
    if (buffer.length === 0) throw new Error("Empty file");
    if (buffer.length > MAX_FILE_SIZE) throw new Error("File exceeds 10MB limit");

    // Extra MIME magic-byte sniff to make sure the client didn't lie about
    // the mime type. Reject anything that doesn't look like JPEG/PNG/WEBP/PDF.
    const looksLikeJpeg = buffer[0] === 0xFF && buffer[1] === 0xD8 && buffer[2] === 0xFF;
    const looksLikePng = buffer[0] === 0x89 && buffer[1] === 0x50 && buffer[2] === 0x4E && buffer[3] === 0x47;
    const looksLikeWebp =
      buffer[0] === 0x52 && buffer[1] === 0x49 && buffer[2] === 0x46 && buffer[3] === 0x46 &&
      buffer[8] === 0x57 && buffer[9] === 0x45 && buffer[10] === 0x42 && buffer[11] === 0x50;
    const looksLikePdf = buffer[0] === 0x25 && buffer[1] === 0x50 && buffer[2] === 0x44 && buffer[3] === 0x46;
    const mimeOk =
      (data.mimeType === "image/jpeg" && looksLikeJpeg) ||
      (data.mimeType === "image/png" && looksLikePng) ||
      (data.mimeType === "image/webp" && looksLikeWebp) ||
      (data.mimeType === "application/pdf" && looksLikePdf);
    if (!mimeOk) throw new Error("File contents do not match the declared type");

    const extMap: Record<string, string> = {
      "image/jpeg": "jpg",
      "image/png": "png",
      "image/webp": "webp",
      "application/pdf": "pdf",
    };
    const ext = extMap[data.mimeType];
    const rand = crypto.randomUUID();
    const objectPath = `${data.disputeId}/${rand}.${ext}`;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error: upErr } = await supabaseAdmin.storage
      .from("dispute-evidence")
      .upload(objectPath, buffer, {
        contentType: data.mimeType,
        upsert: false,
      });
    if (upErr) throw new Error(upErr.message);

    const { data: recId, error: recErr } = await supabaseAdmin.rpc("record_dispute_evidence" as never, {
      _dispute_id: data.disputeId,
      _uploader_role: role,
      _uploader_id: userId,
      _file_path: objectPath,
      _file_name: data.fileName.replace(/[^\w. \-]/g, "_").slice(0, 200),
      _mime: data.mimeType,
      _size: buffer.length,
      _caption: data.caption ?? null,
    } as never);
    if (recErr) {
      // Best-effort: remove the uploaded object if the metadata insert failed.
      await supabaseAdmin.storage.from("dispute-evidence").remove([objectPath]).catch(() => {});
      throw new Error(recErr.message);
    }

    return { id: recId as unknown as string };
  });

/** Return a short-lived signed URL for a piece of evidence, after checking
 *  the caller is a dispute participant or admin. */
export const getDisputeEvidenceUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ evidenceId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: ev, error } = await supabase
      .from("dispute_evidence")
      .select("id,file_path,dispute_id,disputes(buyer_id,seller_id)")
      .eq("id", data.evidenceId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!ev) throw new Error("Evidence not found");
    const disp = (ev as unknown as { disputes: { buyer_id: string; seller_id: string } | null }).disputes;
    if (!disp) throw new Error("Dispute not found");
    await resolveRole(supabase, userId, disp);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error: sErr } = await supabaseAdmin.storage
      .from("dispute-evidence")
      .createSignedUrl(ev.file_path, 60);
    if (sErr || !signed?.signedUrl) throw new Error(sErr?.message ?? "Could not create signed URL");
    return { url: signed.signedUrl };
  });

/** Admin: request evidence from buyer or seller. */
export const adminRequestDisputeEvidence = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      disputeId: z.string().uuid(),
      target: z.enum(["buyer", "seller"]),
      notes: z.string().max(1000).optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.rpc("admin_request_dispute_evidence" as never, {
      _dispute_id: data.disputeId,
      _target: data.target,
      _notes: data.notes ?? null,
    } as never);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin: move to under review. */
export const adminMoveDisputeUnderReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ disputeId: z.string().uuid(), notes: z.string().max(1000).optional().nullable() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.rpc("admin_mark_dispute_under_review" as never, {
      _dispute_id: data.disputeId,
      _notes: data.notes ?? null,
    } as never);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin: close without financial action. */
export const adminCloseDisputeNoAction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ disputeId: z.string().uuid(), notes: z.string().trim().min(5).max(1000) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.rpc("admin_close_dispute_no_action" as never, {
      _dispute_id: data.disputeId,
      _notes: data.notes,
    } as never);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin: resolve in seller's favour — creates a Stripe Transfer via the shared
 *  performOrderPayout helper, then finalizes the dispute row. */
export const adminResolveDisputeSeller = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ disputeId: z.string().uuid(), notes: z.string().trim().min(5).max(1000) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");

    const { data: dispute, error } = await supabase
      .from("disputes")
      .select("id,order_id,status")
      .eq("id", data.disputeId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!dispute) throw new Error("Dispute not found");
    if (!ACTIVE_STATUSES.includes(dispute.status as typeof ACTIVE_STATUSES[number])) {
      throw new Error("Dispute is not active");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    await supabaseAdmin.rpc("_log_dispute_event" as never, {
      _dispute_id: dispute.id,
      _order_id: dispute.order_id,
      _actor_id: userId,
      _actor_role: "admin",
      _event_type: "payout_attempted",
      _metadata: { source: "dispute_resolved_seller" },
    } as never);

    const { performOrderPayout } = await import("./order-payout.server");
    let payoutResult;
    try {
      payoutResult = await performOrderPayout(dispute.order_id, {
        source: "dispute_resolved_seller",
        actorId: userId,
        reference: `dispute_${dispute.id}`,
        notes: data.notes,
        overrideDisputeCheck: true,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      await supabaseAdmin.rpc("_log_dispute_event" as never, {
        _dispute_id: dispute.id,
        _order_id: dispute.order_id,
        _actor_id: userId,
        _actor_role: "admin",
        _event_type: "payout_failed",
        _metadata: { error: msg },
      } as never);
      throw new Error("Payout failed — dispute kept open, please retry");
    }

    if ("blocked" in payoutResult && payoutResult.blocked) {
      throw new Error("Payout blocked — dispute kept open");
    }

    // Finalize dispute row now that transfer succeeded
    const { error: finErr } = await supabaseAdmin.rpc("admin_finalize_dispute_seller" as never, {
      _dispute_id: dispute.id,
      _notes: data.notes,
    } as never);
    if (finErr) throw new Error(finErr.message);

    await supabaseAdmin.rpc("_log_dispute_event" as never, {
      _dispute_id: dispute.id,
      _order_id: dispute.order_id,
      _actor_id: userId,
      _actor_role: "admin",
      _event_type: "payout_succeeded",
      _metadata: { transfer_id: (payoutResult as { transferId: string }).transferId },
    } as never);

    return { ok: true };
  });
